import React from 'react';
import ModalLayout from '../layouts/ModalLayout';

type AffiliateTermsModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

export default function AffiliateTermsModal({
  open,
  setOpen,
}: AffiliateTermsModalProps): JSX.Element {
  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className="relative z-50 h-[600px] max-w-4xl rounded-[20px] bg-secondary-50 p-10 py-14">
      <div className="h-full overflow-auto">
        <h1 className="text-[28px] font-semibold">Affiliate Terms & Conditions – Vera Connect</h1>
        <p className="mt-2 text-sm text-gray-600">Last updated: 21-07-2025</p>
        <p className="mt-4">
          By joining the Vera Connect affiliate program, you agree to the terms below. These apply to the referral of new groups via your personal affiliate code, and the commission earned from resulting paid subscriptions.
        </p>
        
        <div className="mt-6 space-y-4">
          <div>
            <h3 className="text-lg font-semibold mb-2">1. Participation</h3>
            <div className="text-sm text-gray-700 space-y-2">
              <p>The program is open to individuals and informal or formal groups, such as student houses, sports teams, clubs, or associations.</p>
              <p>If you are acting on behalf of a group or organization, you declare that you are authorized to do so.</p>
              <p>You must be at least 18 years old to join, unless you have permission from a legal guardian or group representative.</p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">2. Commission, Payout & Program Limits</h3>
            <div className="text-sm text-gray-700 space-y-2">
              <p>You will earn 25% commission on the net subscription revenue of any group that signs up using your affiliate code and upgrades to a paid plan.</p>
              <p>New groups can be referred until December 31, 2025.</p>
              <p>Commission is paid out as long as the referred group maintains a paid subscription, with a hard program end date of June 30, 2027. No commission is paid after this date, regardless of when a group was referred.</p>
              <p>Payouts are made quarterly, provided you have earned at least €25 in commission.</p>
              <p>Payments are made via bank transfer or through a third-party payout provider. Transaction fees may be deducted.</p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">3. Legal & Tax Responsibility</h3>
            <div className="text-sm text-gray-700 space-y-2">
              <p>You are responsible for reporting any earned income to your local tax authorities.</p>
              <p>If you represent a legal entity (e.g., an association or non-profit), you must account for affiliate income within your own financial administration in accordance with applicable regulations.</p>
              <p>If payment is made to a non-legal entity (e.g., an informal student group), a single representative will receive the payout and is legally responsible for any internal distribution.</p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">4. Fraud & Misuse</h3>
            <div className="text-sm text-gray-700 space-y-2">
              <p>Creating fake accounts or groups to generate commission is strictly prohibited.</p>
              <p>Any misuse—such as self-referrals or fraudulent signups—may result in removal from the affiliate program and forfeiture of unpaid commissions.</p>
              <p>Vera Connect reserves the right to investigate suspicious activity and request additional information if necessary.</p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">5. Termination of Participation or Program</h3>
            <div className="text-sm text-gray-700 space-y-2">
              <p>You may leave the affiliate program at any time via the affiliate dashboard or by contacting us via email.</p>
              <p>Vera Connect reserves the right to change or end the program in the future. If this happens, we will notify you clearly and at least 30 days in advance via email or your dashboard.</p>
              <p>Any commission already earned prior to termination will still be paid out according to the terms (up to June 30, 2027 at the latest).</p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">6. Contact</h3>
            <p className="text-sm text-gray-700">
              Questions? Reach out via contact@veraconnect.nl or use the affiliate dashboard to get in touch.
            </p>
          </div>
        </div>
      </div>
    </ModalLayout>
  );
}
